import { useContext, useEffect, useMemo } from 'react';
import { AppContext } from '@/contexts/app-context';
import { useSupremeCards } from '@/hooks/use-supreme-cards';
import { Image } from '../../image/image';

type CardFields = {
    cardApplication: {
        stepOnePersonalData: {
            title: string;
            content: string;
        };
        stepTwoSetLimit: {
            title: string;
            content: string;
        };
        stepThreeGranted: {
            title: string;
            content: string;
            link: {
                enabled: boolean;
                text: string;
            };
        };
        error: {
            title: string;
            content: string;
        };
    };
    rejection: {
        stepThreeRejection: {
            title: string;
            content: string;
        };
    };
};

export type SupremeCardInfoProps = {
    name: string;
    cardInfoTexts: {
        CLASSIC: CardFields;
        GOLD: CardFields;
        WOMAN: CardFields;
        WORLD: CardFields;
    } | null;
};

export const SupremeCardInfo = (props: SupremeCardInfoProps) => {
    const { supremeCardApplication, setSupremeCardApplication } = useContext(AppContext);
    const { supremeCards } = useSupremeCards();

    const currentCard = useMemo(
        () => supremeCards.data?.find(({ api_name: apiName }) => apiName === supremeCardApplication.selectedCard),
        [supremeCards, supremeCardApplication]
    );

    const cardTexts = useMemo(() => {
        if (!props.cardInfoTexts || !supremeCardApplication.selectedCard) {
            return null;
        }
        return props.cardInfoTexts[supremeCardApplication.selectedCard as keyof typeof props.cardInfoTexts] ?? null;
    }, [supremeCardApplication, props]);

    const getCurrentData = useMemo(() => {
        switch (supremeCardApplication.step) {
            case 1:
                return cardTexts?.cardApplication?.stepOnePersonalData;
            case 2:
                return cardTexts?.cardApplication?.stepTwoSetLimit;
            case 3:
                return cardTexts?.cardApplication?.stepThreeGranted;
        }
    }, [supremeCardApplication.step, cardTexts]);

    useEffect(() => {
        setSupremeCardApplication({
            progressText: getCurrentData,
        });
    }, [supremeCardApplication.step, supremeCardApplication.selectedCard]);

    const isEmpty = !supremeCardApplication?.progressText || !currentCard?.title;

    return (
        <div data-component="supreme-card-info" data-empty={isEmpty} className="max-md:r-hidden">
            {!isEmpty && (
                <>
                    {currentCard ? <Image src={currentCard?.image?.src} height={252} width={160} size="225xAUTO" noShadow /> : null}
                    <h2 className="r-mb-2 r-text-xl r-font-medium">{supremeCardApplication?.progressText?.title}</h2>
                    <div className="styled-list" dangerouslySetInnerHTML={{ __html: supremeCardApplication?.progressText?.content }}></div>
                </>
            )}
        </div>
    );
};
